import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
  faPlus,
  faAnglesLeft,
  faAnglesRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import "./ColorCard.scss";
import { useProductStore } from "../../../zustand/productStore";
import { convertToBase64, getColorName, isBase64 } from "utils/imagesAndColors";
import { ColorVariant, SizeCreate } from "interfaces/IProduct";
import ImageModal from "components/general/ImageModal";
import {
  errorColor,
  primaryColor,
  primaryColorOpacity,
  secondaryColor,
} from "utils/colors";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { ChromePicker } from "react-color";

interface ColorCardProps {
  colorVariant: ColorVariant;
  onUpdate: (updatedVariant: ColorVariant) => void;
  onDelete: () => void;
  hasError: boolean;
  isReadOnly?: boolean;
  onImageDelete?: (imageName: string) => void;
  showValidationErrors?: boolean; // Nueva prop para mostrar errores de validación
}

const ColorCard: React.FC<ColorCardProps> = ({
  colorVariant,
  onUpdate,
  onDelete,
  hasError,
  isReadOnly = false,
  onImageDelete = () => {},
  showValidationErrors = false,
}) => {
  const [isMinimized, setIsMinimized] = useState(isReadOnly);
  const { sizes } = useProductStore();
  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const totalItems = colorVariant.images.length + 1;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Validación de descripción
  const isDescriptionEmpty = !colorVariant.sizes[0]?.description || colorVariant.sizes[0]?.description.trim() === '';
  const showDescriptionError = showValidationErrors && isDescriptionEmpty;

  // Estado para controlar la navegación de tallas y stocks
  const [currentSizeIndex, setCurrentSizeIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Calcular cuántos elementos caben en el espacio disponible
  const calculateItemsPerView = () => {
    if (containerWidth === 0) return 8; // Valor por defecto más alto
    const itemWidth = 80; // Ancho aproximado de cada elemento sin gaps
    const availableWidth = containerWidth - 100; // Restar espacio para botones
    return Math.max(4, Math.floor(availableWidth / itemWidth));
  };
  
  const itemsPerView = calculateItemsPerView();

  /**
   * Effect para medir el ancho del contenedor y recalcular itemsPerView
   */
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  /**
   * Navega a la siguiente página de tallas/stocks
   */
  const handleNextSizes = () => {
    const totalSizes = sizes?.filter((size) => size.status === 1).length || 0;
    const maxIndex = Math.max(0, totalSizes - itemsPerView);
    setCurrentSizeIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  /**
   * Navega a la página anterior de tallas/stocks
   */
  const handlePrevSizes = () => {
    setCurrentSizeIndex((prev) => Math.max(prev - 1, 0));
  };

  /**
   * Obtiene las tallas visibles según el índice actual
   */
  const getVisibleSizes = () => {
    const activeSizes = sizes?.filter((size) => size.status === 1) || [];
    return activeSizes.slice(currentSizeIndex, currentSizeIndex + itemsPerView);
  };

  /**
   * Moves to the previous image in the carousel.
   * Sets the current image index to the maximum of 0 or the previous index minus 1.
   */
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  };

  /**
   * Moves to the next image in the carousel.
   * Sets the current image index to the minimum of (totalItems - 5) or the previous index plus 1.
   */
  const handleNextImage = () => {
    setCurrentImageIndex((prev) => Math.min(totalItems - 5, prev + 1));
  };

  /**
   * Handles image selection and converts selected images to base64.
   * Updates the color variant with the new images.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event.
   */
  const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const base64Images = await Promise.all(
        Array.from(files).map((file) => convertToBase64(file))
      );
      const updatedImages = [...colorVariant.images, ...base64Images];
      onUpdate({ ...colorVariant, images: updatedImages });
    }
  };
  /**
   * Handles the deletion of an image from a color variant
   * @param {number} indexToDelete - The index of the image to be deleted from the array
   *
   * The function:
   * 1. Gets the image URL/data at the specified index
   * 2. Extracts the filename from the URL using split and pop
   * 3. Notifies parent component about the deleted image
   * 4. Creates new array without the deleted image
   * 5. Updates the color variant with the new images array
   */
  const handleDeleteImage = (indexToDelete: number) => {
    const imageToDelete = colorVariant.images[indexToDelete];
    onImageDelete(imageToDelete.split("/").pop() ?? "");
    const updatedImages = colorVariant.images.filter(
      (_, index) => index !== indexToDelete
    );
    onUpdate({ ...colorVariant, images: updatedImages });
  };

  /**
   * Updates the price for all sizes of the color variant.
   * @param {string} value - The new price value.
   */
  const handlePriceChange = (value: string) => {
    const updatedSizes: SizeCreate[] = colorVariant.sizes.map((size) => ({
      ...size,
      price: parseFloat(value),
    }));
    onUpdate({ ...colorVariant, sizes: updatedSizes });
  };
  const handleNameColorChange = (value: string) => {
    onUpdate({ ...colorVariant, colorName: value });
  };

  /**
   * Updates the stock for a specific size of the color variant.
   * @param {string} sizeName - The name of the size to update.
   * @param {string} value - The new stock value.
   */
  const handleStockChange = (sizeName: string, value: string) => {
    let updatedSizes;
    // Buscar la talla por description en lugar de name
    const existingSize = colorVariant.sizes.find(
      (size) => size.size === sizeName
    );

    if (existingSize) {
      updatedSizes = colorVariant.sizes.map((size) =>
        size.size === sizeName ? { ...size, stock: parseInt(value) } : size
      );
    } else {
      updatedSizes = [
        ...colorVariant.sizes,
        {
          size: sizeName,
          stock: parseInt(value),
          price: colorVariant.sizes[0].price,
          description: colorVariant.sizes[0].description,
        },
      ];
    }

    onUpdate({ ...colorVariant, sizes: updatedSizes });
  };

  /**
   * Updates the description for all sizes of the color variant.
   * @param {string} value - The new description value.
   */
  const handleDescriptionChange = (value: string) => {
    const updatedSizes: SizeCreate[] = colorVariant.sizes.map((size) => ({
      ...size,
      description: value,
    }));
    onUpdate({ ...colorVariant, sizes: updatedSizes });
  };

  /**
   * Updates the description for a specific size of the color variant.
   * @param {string} sizeName - The name of the size to update.
   * @param {string} value - The new description value.
   */
  const handleSizeDescriptionChange = (sizeName: string, value: string) => {
    const updatedSizes: SizeCreate[] = colorVariant.sizes.map((size) =>
      size.size === sizeName ? { ...size, description: value } : size
    );
    onUpdate({ ...colorVariant, sizes: updatedSizes });
  };

  return (
    <div
      style={{
        backgroundColor: primaryColorOpacity,
        border: `2px solid ${hasError || showDescriptionError ? errorColor : primaryColor}`,
        borderRadius: "9px",
        padding: "10px 20px 0 20px",
        marginBottom: "20px",
        boxShadow: "0 10px 20px 0 rgba(0, 0, 0, 0.4)",
      }}
    >
      <Row className="align-items-center mb-3">
        {isMinimized ? (
          <>
            <Col xs="1">
              <FontAwesomeIcon
                icon={faChevronDown}
                onClick={toggleMinimize}
                style={{
                  cursor: "pointer",
                  color: secondaryColor,
                  height: "20px",
                }}
              />
            </Col>
            <Col xs="1">
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: colorVariant.color,
                  marginRight: "10px",
                }}
              ></div>
            </Col>
            <Col xs="2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip
                    className="custom-tooltip"
                    id={`tooltip-${colorVariant.colorName}`}
                  >
                    {colorVariant.colorName}
                  </Tooltip>
                }
              >
                <span
                  style={{
                    marginRight: "20px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {colorVariant.colorName}
                </span>
              </OverlayTrigger>
            </Col>

            <Col xs="2">
              <Form.Control
                type="text"
                value={`$${colorVariant.sizes[0]?.price || 0}`}
                disabled
                style={{ width: "100px", backgroundColor: "white" }}
              />
            </Col>
            <Col xs="4">
              {colorVariant.images.length > 0 ? (
                colorVariant.images.slice(0, 4).map((image, index) => (
                  <img
                    key={`${colorVariant.color}-${index}`}
                    src={
                      isBase64(image)
                        ? `data:image/jpeg;base64,${image}`
                        : image
                    }
                    alt={`${colorVariant.color} variant - ${index + 1}`}
                    style={{
                      width: "3rem",
                      height: "3rem",
                      objectFit: "cover",
                      marginRight: "1rem",
                      borderRadius: "1rem",
                    }}
                  />
                ))
              ) : (
                <label htmlFor="no-images">No hay imágenes agregadas</label>
              )}
            </Col>
            <Col xs="2">
              {!isReadOnly && (
                <Button
                  style={{ width: "100%" }}
                  variant="secondary"
                  className="fw-bold"
                  onClick={onDelete}
                >
                  Borrar color
                </Button>
              )}
            </Col>
          </>
        ) : (
          <>
            <Col xs="1">
              <FontAwesomeIcon
                icon={faChevronUp}
                onClick={toggleMinimize}
                style={{
                  cursor: "pointer",
                  color: secondaryColor,
                  height: "20px",
                }}
              />
            </Col>
            <Col
              xs="9"
              className="d-flex justify-content-center align-items-center"
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: colorVariant.color,
                  cursor: isReadOnly ? "default" : "pointer",
                }}
                onClick={() =>
                  !isReadOnly && setShowColorPicker(!showColorPicker)
                }
              >
                <div
                  style={{
                    position: "absolute",
                    zIndex: 2,
                  }}
                ></div>
              </div>

              {isReadOnly ? (
                <span style={{ marginLeft: "20px", fontWeight: "bold" }}>
                  {colorVariant.colorName}
                </span>
              ) : (
                <Form.Control
                  type="text"
                  style={{ marginLeft: "1rem", width: "25%" }}
                  onChange={(e) => handleNameColorChange(e.target.value)}
                  placeholder="Nombre del color"
                  value={colorVariant.colorName || ""}
                />
              )}
            </Col>
            <Col xs="2">
              {!isReadOnly && (
                <Button
                  style={{ width: "100%" }}
                  variant="secondary"
                  className="fw-bold"
                  onClick={onDelete}
                >
                  Borrar color
                </Button>
              )}
            </Col>
          </>
        )}
      </Row>
      {!isMinimized && (
        <div
          className={`content ${isMinimized ? "minimized" : ""}`}
          style={{ height: isMinimized ? "0" : "auto" }}
        >
          <Form.Group className="mb-4">
            <Row className="align-items-center">
              <Col xs={2}>
                <Form.Label className="mb-0 fw-bold">
                  Precio:
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Precio que aparecera para los clientes del producto
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
              </Col>

              <Col xs={4} style={{ marginLeft: "-3rem" }}>
                <Form.Control
                  type="text"
                  onChange={(e) =>
                    !isReadOnly && handlePriceChange(e.target.value)
                  }
                  placeholder="Precio (MXN)"
                  readOnly={isReadOnly}
                  value={colorVariant.sizes[0]?.price || ""}
                />
              </Col>
            </Row>
          </Form.Group>

          {/* Contenedor relativo para posicionar los botones */}
          <div style={{ position: "relative" }}>
            <Row className="mb-4 align-items-end">
              <Col xs={12} md="auto" style={{ marginBottom: "0.4rem" }}>
                <Form.Label className="fw-bold" style={{ width: "4.5rem" }}>
                  Talla:
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Tallas disponibles
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
              </Col>
              <Col>
                <div ref={containerRef} className="d-flex" style={{ flex: 1, marginLeft: "3rem", marginRight: "3rem" }}>
                  {getVisibleSizes().map((size) => (
                    <div key={size.id} style={{ flex: 1, minWidth: "0" }}>
                      <Form.Control
                        type="text"
                        value={size.description}
                        readOnly
                        className="size-input"
                        style={{ textAlign: "center", width: "100%" }}
                      />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            <Row className="mb-4 align-items-end">
              <Col xs={12} md="auto" style={{ marginBottom: "0.4rem" }}>
                <Form.Label className="fw-bold" style={{ width: "4.5rem" }}>
                  Stock:
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Stock disponible del producto
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
              </Col>
              <Col>
                <div className="d-flex" style={{ flex: 1, marginLeft: "3rem", marginRight: "3rem" }}>
                  {getVisibleSizes().map((size) => (
                    <div key={size.id} style={{ flex: 1, minWidth: "0" }}>
                      <Form.Control
                        type="number"
                        onChange={(e) => {
                          !isReadOnly &&
                            handleStockChange(size.description, e.target.value);
                        }}
                        placeholder="0"
                        className="stock-input"
                        min="0"
                        max="999"
                        readOnly={isReadOnly}
                        value={
                          colorVariant.sizes.find((s) => s.size === size.description)
                            ?.stock ?? "0"
                        }
                        style={{ textAlign: "center", width: "100%" }}
                      />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            {/* Botón izquierdo centrado verticalmente */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handlePrevSizes}
              disabled={currentSizeIndex === 0}
              style={{
                position: "absolute",
                left: "4.5rem", // Posicionado en el espacio libre a la izquierda
                top: "50%",
                transform: "translateY(-50%)",
                minWidth: "40px",
                zIndex: 10
              }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>

            {/* Botón derecho centrado verticalmente */}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleNextSizes}
              disabled={currentSizeIndex + itemsPerView >= (sizes?.filter((size) => size.status === 1).length || 0)}
              style={{
                position: "absolute",
                right: "0.5rem", // Más hacia la derecha, en el espacio libre
                top: "50%",
                transform: "translateY(-50%)",
                minWidth: "40px",
                zIndex: 10
              }}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">
              Descripción:
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip className="custom-tooltip">
                    Descripción del producto
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              onChange={(e) =>
                !isReadOnly && handleDescriptionChange(e.target.value)
              }
              as="textarea"
              rows={3}
              readOnly={isReadOnly}
              value={colorVariant.sizes[0]?.description || ""}
              style={{
                border: showDescriptionError ? `2px solid ${errorColor}` : undefined,
                boxShadow: showDescriptionError ? `0 0 0 0.2rem rgba(220, 53, 69, 0.25)` : undefined
              }}
            />
            {showDescriptionError && (
              <div 
                style={{
                  color: errorColor,
                  fontSize: "0.875rem",
                  marginTop: "0.25rem",
                  fontWeight: "500"
                }}
              >
                ⚠️ La descripción es obligatoria para el color "{colorVariant.colorName}"
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">
              Imágenes representativas:
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip className="custom-tooltip">
                    Imágenes del producto
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
              </OverlayTrigger>
            </Form.Label>

            <div
              style={{
                padding: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "9.5rem",
                }}
              >
                {currentImageIndex > 0 && (
                  <FontAwesomeIcon
                    icon={faAnglesLeft}
                    onClick={handlePrevImage}
                    style={{
                      cursor: "pointer",
                      color: secondaryColor,
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                    }}
                  />
                )}
              </div>
              {[...Array(5)].map((_, index) => {
                const itemIndex = currentImageIndex + index;
                if (itemIndex < colorVariant.images.length) {
                  return (
                    <div
                      key={itemIndex}
                      style={{
                        width: "9.5rem",
                        height: "9.5rem",
                        borderRadius: "9px",
                        overflow: "hidden",
                        cursor: "pointer",
                        padding: 0,
                        border: "none",
                        background: "none",
                        position: "relative",
                      }}
                      onClick={() => {
                        setSelectedImageIndex(itemIndex);
                        setModalOpen(true);
                      }}
                    >
                      <img
                        src={
                          isBase64(colorVariant.images[itemIndex])
                            ? `data:image/jpeg;base64,${colorVariant.images[itemIndex]}`
                            : `${
                                colorVariant.images[itemIndex]
                              }?t=${Date.now()}`
                        }
                        alt={`Selected ${itemIndex}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {!isReadOnly && (
                        <Button
                          variant="link"
                          style={{
                            position: "absolute",
                            top: "0.1rem",
                            right: "0.1rem",
                            color: "#614900",
                            borderRadius: "50%",
                            padding: "8px",
                            fontSize: "1.2rem",
                            zIndex: 10,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(itemIndex);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </div>
                  );
                } else if (itemIndex === colorVariant.images.length) {
                  return (
                    !isReadOnly && (
                      <label
                        key="add"
                        style={{ cursor: "pointer" }}
                        aria-label="Add new image"
                      >
                        <div
                          style={{
                            border: "2px solid" + secondaryColor,
                            borderRadius: "9px",
                            backgroundColor: secondaryColor,
                            width: "9.5rem",
                            height: "9.5rem",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: primaryColor,
                              borderRadius: "50%",
                              width: "8rem",
                              height: "8rem",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              color="white"
                              style={{ height: "7rem", fontWeight: "lighter" }}
                            />
                          </div>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={onImageSelect}
                            style={{ display: "none" }}
                          />
                        </div>
                      </label>
                    )
                  );
                }
                return null;
              })}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "9.5rem",
                }}
              >
                {currentImageIndex < totalItems - 5 && (
                  <FontAwesomeIcon
                    icon={faAnglesRight}
                    onClick={handleNextImage}
                    style={{
                      cursor: "pointer",
                      color: secondaryColor,
                      fontSize: "2rem",
                    }}
                  />
                )}
              </div>
            </div>
          </Form.Group>
        </div>
      )}
      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={colorVariant.images}
        selectedIndex={selectedImageIndex}
        onSelectImage={setSelectedImageIndex}
        isReadOnly={isReadOnly}
      />
    </div>
  );
};

export default ColorCard;